import { useEffect, useState } from 'react';
import { Monitor, Mail, Link2, Check } from 'lucide-react';

import unifyLogo from '@/assets/unify-logo.png';
import { useMobileNotice } from '@/lib/mobileNotice';
import { useRequestDesktopLink } from '@/hooks/useRequestDesktopLink';
import { useToast } from '@/components/ui/toast';

/**
 * Full-screen interstitial shown to mobile users (Unify is optimised for
 * desktop). "Email me the link" stamps a Loops property server-side (a Loops
 * workflow sends the email — NEA-5808); "Copy link" copies the current URL.
 * "Continue on mobile anyway" dismisses it for the session (it returns on the next page load).
 */
export function MobileInterstitial() {
	const { dismiss } = useMobileNotice();
	const { toast } = useToast();
	const { request, status } = useRequestDesktopLink();
	const [copied, setCopied] = useState(false);

	// Full-screen takeover: lock background scroll while shown. This also clamps
	// the mobile layout viewport to the visible width — otherwise the desktop
	// dashboard behind can expand the layout viewport wider than the screen,
	// which pushes this centred overlay's content off the right edge.
	useEffect(() => {
		const { documentElement: html, body } = document;
		const prevHtml = html.style.overflow;
		const prevBody = body.style.overflow;
		html.style.overflow = 'hidden';
		body.style.overflow = 'hidden';
		return () => {
			html.style.overflow = prevHtml;
			body.style.overflow = prevBody;
		};
	}, []);

	const url = typeof window !== 'undefined' ? window.location.href : '';

	const handleEmail = async () => {
		const ok = await request();
		if (ok) {
			toast({
				title: 'Link sent',
				description: "Check your inbox — we've emailed you a link to open Unify on desktop.",
				variant: 'success'
			});
		} else {
			toast({ title: "Couldn't send the link", description: 'Please try again, or copy the link instead.', variant: 'destructive' });
		}
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			toast({ title: 'Link copied', variant: 'success' });
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast({ title: "Couldn't copy link", variant: 'destructive' });
		}
	};

	return (
		<div className="fixed inset-0 z-[100] overflow-y-auto bg-background">
			<div
				aria-hidden
				className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-unify opacity-20 blur-3xl"
			/>
			<div
				aria-hidden
				className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-gradient-unify opacity-10 blur-3xl"
			/>

			<div className="relative flex min-h-full flex-col px-6 py-10">
				<div className="flex items-center">
					<img src={unifyLogo} alt="Unify by NearSt" className="h-12 w-auto" />
				</div>

				<div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
					<div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-unify shadow-unify">
						<Monitor className="h-6 w-6 text-white" strokeWidth={2.25} />
					</div>

					<h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-foreground">Unify is best on desktop</h1>
					<p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
						Your dashboard is optimised for larger screens. For the full experience — managing orders, ads, channels and your
						Local Potential score — open Unify on your laptop.
					</p>

					<div className="mt-8 space-y-3">
						<button
							type="button"
							onClick={handleEmail}
							disabled={status === 'sending'}
							className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-primary-foreground shadow-unify transition-all hover:shadow-unify-strong active:translate-y-px disabled:cursor-not-allowed disabled:opacity-70"
						>
							<Mail className="h-4 w-4" />
							{status === 'sending' ? 'Sending…' : status === 'sent' ? 'Link sent' : 'Email me the link'}
						</button>
						<button
							type="button"
							onClick={handleCopy}
							className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-white text-[15px] font-semibold text-foreground transition-colors hover:bg-muted/60"
						>
							{copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
							{copied ? 'Copied' : 'Copy link'}
						</button>
					</div>

					<button
						type="button"
						onClick={dismiss}
						className="mx-auto mt-8 text-sm font-medium text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
					>
						Continue on mobile anyway
					</button>
				</div>
			</div>
		</div>
	);
}
